import type ApiError from "utils/errors/apiError";

import { getSlackChannels, multiGetSlackChannels, type TSlackChannel } from "api/chats";
import constate from "constate";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useFetchedState } from "hooks/useFetchedState";
import { useThrottledBulkFetch } from "hooks/useThrottledBulkFetch";
import { Map } from "immutable";
import { useCallback, useEffect, useState } from "react";
import { notEmpty } from "utils/comparison";

const useSlackChannels = () => {
  const { user } = useAuthenticatedUser();
  const [missingScopes, setMissingScopes] = useState<null | string>(null);

  const onError = useCallback((error: ApiError) => {
    // Currently there is no demand to show this error to the user
    if (error.errorId === "chatsService.missingScopes") {
      setMissingScopes((error.params?.needed as string) || null);
    }
  }, []);

  // We suppress all the errors because we don׳t want to show any channel related errors to the user
  // Some companies might not have slack integration set up, or has missing scopes, so we just silently fail
  const {
    data: channels,
    fetchingState,
    isLoading,
    loadData: loadChannels,
    setData: setChannels,
  } = useFetchedState(getSlackChannels, { onError, suppressError: true });

  const { itemsById: multiChannels, loadIds: multiLoadChannels } = useThrottledBulkFetch(multiGetSlackChannels, {
    suppressError: true,
    throttleTime: 50,
    withArchived: true,
  });

  const addChannelsToContext = useCallback(
    (newChannels: TSlackChannel[]) => {
      const missingChannels = newChannels.filter((channel) => !channels?.has(channel.id));
      if (!missingChannels.length) return;

      setChannels((curr) => {
        let updated = curr || Map<string, TSlackChannel>();
        missingChannels.forEach((channel) => {
          updated = updated.set(channel.id, channel);
        });
        return updated;
      });
    },
    [setChannels, channels],
  );

  const multiLoadChannelsWrapper = useCallback(
    async (ids: string[]) => {
      if (isLoading) return;

      await multiLoadChannels(ids);
    },
    [isLoading, multiLoadChannels],
  );

  useEffect(() => {
    if (multiChannels.size) {
      const channelsArray = multiChannels.valueSeq().toArray().filter(notEmpty);
      addChannelsToContext(channelsArray);
    }
  }, [multiChannels, addChannelsToContext]);

  useEffect(() => {
    if (!user?.isAdmin) return;

    void loadChannels();
  }, [loadChannels, user?.isAdmin]);

  const isChannelsFetchDone = !user?.isAdmin || fetchingState === "Loaded" || fetchingState === "Error";

  return {
    actions: { loadChannels: multiLoadChannelsWrapper },
    state: { channels, isChannelsFetchDone, isLoading, missingScopes },
  };
};

export const [SlackChannelsProvider, useSlackChannelsContext] = constate(useSlackChannels);
